/**
 * Shared 0DTE vertical-spread configuration: DB loader + rating cfg builder.
 *
 * **Source of truth**
 * - Per-account values: `account_strategy_settings` / `account_strategy_defaults`
 * - Agent code defaults: Rust `vs0_defaults()` in plugins/vertical_spreads_0dte/defaults.rs
 *
 * Frontend no longer ships a parallel DEFAULTS map (removed / deprecated).
 */

import { apiGet } from "@/lib/api";
import { cfgFromEntries } from "@/lib/strategy-config-keys";
import { parsePoints } from "@/components/slider";
import { OPTION_MULTIPLIER } from "@/lib/option-multiplier";

// ---------------------------------------------------------------------------
// Config loader — account_strategy_settings only
// ---------------------------------------------------------------------------

interface ConfigEntry {
  key: string;
  value: string;
}

/** Load per-account 0DTE VS config from `account_strategy_settings`. Code defaults
 *  are not mirrored in the browser; the agent uses Rust `vs0_defaults()`.
 *  Without an account hash, returns `{}`. */
export async function loadVsConfig(accountHash?: string): Promise<Record<string, string>> {
  if (!accountHash) return {};
  const mainEntries = await apiGet<ConfigEntry[]>(`/strategies/account-settings/vertical_spreads_0dte?account=${encodeURIComponent(accountHash)}`).catch(() => []);
  return applyEnableDefaults(cfgFromEntries(mainEntries));
}

// ---------------------------------------------------------------------------
// Sizing — per-direction per-spread max-loss budget
// ---------------------------------------------------------------------------

/** Per-direction per-spread max-loss budget in dollars. */
export interface PositionSizes {
  bull: number; // bull put
  bear: number; // bear call
}

/** Per-spread dollar budget for the active sizing method (mirrors the agent's
 *  `rating::active_size_method`): percent → pct × account value, dollars → the
 *  entered amount, fixed-contracts → 0 (count-based, no budget). Global, so both
 *  directions get the same value. */
export function positionSizesFor(cfg: Record<string, string>, accountValue: number): PositionSizes {
  const on = (k: string, def: boolean) => (cfg[k] !== undefined ? cfg[k] !== "false" : def);
  const dir = (p: "bull" | "bear") => {
    if (on(`vs0_${p}_size_contracts_enabled`, false)) return 0;
    if (on(`vs0_${p}_size_dollars_enabled`, false)) return Number(cfg[`vs0_${p}_size_dollars`]) || 0;
    return ((Number(cfg[`vs0_${p}_size_pct`]) || 0) / 100) * accountValue;
  };
  return { bull: dir("bull"), bear: dir("bear") };
}

/** Select the budget for a given spread direction. */
export function positionSizeFor(sizes: PositionSizes, spreadType: "bull_put" | "bear_call"): number {
  return spreadType === "bull_put" ? sizes.bull : sizes.bear;
}

/** Contract count for one spread: the fixed count when that direction sizes by
 *  a fixed number of contracts, otherwise floor(budget / max loss per contract).
 *  `maxLossPerShare` is the spread's max loss per share (× OPTION_MULTIPLIER =
 *  per contract). Mirrors the Rust engine. */
export function contractsForSpread(
  cfg: Record<string, string>,
  sizes: PositionSizes,
  spreadType: "bull_put" | "bear_call",
  maxLossPerShare: number,
): number {
  const side = spreadType === "bull_put" ? "bull" : "bear";
  if (cfg[`vs0_${side}_size_contracts_enabled`] === "true") {
    return Math.max(0, Math.trunc(Number(cfg[`vs0_${side}_size_contracts`] ?? "1") || 0));
  }
  const budget = positionSizeFor(sizes, spreadType);
  const mlpc = maxLossPerShare * OPTION_MULTIPLIER;
  return mlpc > 0 && budget > 0 ? Math.floor(budget / mlpc) : 0;
}

// ---------------------------------------------------------------------------
// Rating config builder — converts string config map to typed rateOption() input
// ---------------------------------------------------------------------------

export interface RatingCfg {
  baSpreadPctWeight: number; baSpreadPctEnabled: boolean;
  probItmWeight: number; probItmEnabled: boolean;
  hvPctWeight: number; hvPctEnabled: boolean;
  hvPctPoints: [number, number][]; hvPctLeftY: number; hvPctRightY: number;
  oiEnabled: boolean;
  /** Open-interest go/no-go gate (ZoneSlider). `minOi` is the threshold; the
   * left/right zones decide whether OI below/above it is accepted (>=0) or
   * rejected (<0). Default: reject below, accept above (a min floor). Applied to
   * BOTH legs of a spread. From `vs0_min_oi` / `vs0_min_oi_left_y` / `_right_y`. */
  minOi: number; minOiLeftY: number; minOiRightY: number;
  baSpreadPctPoints: [number, number][]; baSpreadPctLeftY: number; baSpreadPctRightY: number;
  probItmPoints: [number, number][]; probItmLeftY: number; probItmRightY: number;
  eProbItmWeight: number; eProbItmEnabled: boolean;
  eProbItmPoints: [number, number][]; eProbItmLeftY: number; eProbItmRightY: number;
  pProbItmWeight: number; pProbItmEnabled: boolean;
  pProbItmPoints: [number, number][]; pProbItmLeftY: number; pProbItmRightY: number;
  /** Min of selected P/eP/pP curve scores. `minPitmSources` is `p,ep,pp`. */
  minPitmWeight: number; minPitmEnabled: boolean;
  minPitmPoints: [number, number][]; minPitmLeftY: number; minPitmRightY: number;
  minPitmSources: string;
  rorPoints: [number, number][]; rorLeftY: number; rorRightY: number;
  rorWeight: number;
  netCreditPoints: [number, number][]; netCreditLeftY: number; netCreditRightY: number;
  netCreditWeight: number;
  otmPoints: [number, number][]; otmLeftY: number; otmRightY: number;
  otmWeight: number; otmEnabled: boolean;
  absReturnPoints: [number, number][]; absReturnLeftY: number; absReturnRightY: number;
  absReturnWeight: number; absReturnEnabled: boolean;
  maxWidthPoints: [number, number][]; maxWidthLeftY: number; maxWidthRightY: number;
  maxWidthWeight: number; maxWidthEnabled: boolean;
}

/** Parse a config string to number, falling back to a default if NaN or empty. */
function num(val: string | undefined, def: number): number {
  if (val === undefined || val === '') return def;
  const n = Number(val);
  return isNaN(n) ? def : n;
}

/** Per-parameter enable key (hidden ⟺ `<key>="false"` in the per-account config).
 *  Mirrors the Rust registry's `<prefix>_enabled`. */
export const VS0_PARAM_ENABLE_KEY: Record<string, string> = {
  fomc: "vs0_avoid_fomc_enabled",
  fomcMinutes: "vs0_avoid_fomc_minutes_enabled",
  noDte: "vs0_no_dte_enabled", computeHv: "vs0_compute_hv_enabled", computePItm: "vs0_compute_pitm_enabled",
  junkQuote: "vs0_junk_quote_enabled", minOi: "vs0_min_oi_enabled", hvPct: "vs0_hv_pct_enabled", baSpreadPct: "vs0_ba_spread_pct_enabled", probItm: "vs0_prob_itm_enabled",
  eProbItm: "vs0_e_prob_itm_enabled", pProbItm: "vs0_p_prob_itm_enabled", minPitm: "vs0_min_pitm_enabled", maxWidth: "vs0_max_spread_width_enabled",
  netCredit: "vs0_net_credit_enabled", ror: "vs0_ror_enabled",
  otm: "vs0_otm_enabled", absReturn: "vs0_abs_return_enabled",
  rating: "vs0_rating_enabled",
  // Sizing rows. The three contract-sizing methods are a mutually-exclusive
  // group (exactly one visible = active); the agent reads the same flags.
  totalExposure: "vs0_total_exposure_enabled", callPutSplit: "vs0_call_put_split_enabled",
  totalExposureDollars: "vs0_total_exposure_dollars_enabled",
  // Per-direction trade size — each side (Put = bull, Call = bear) picks one of %/$/#.
  bullSizePct: "vs0_bull_size_pct_enabled", bullSizeDollars: "vs0_bull_size_dollars_enabled", bullSizeContracts: "vs0_bull_size_contracts_enabled",
  bearSizePct: "vs0_bear_size_pct_enabled", bearSizeDollars: "vs0_bear_size_dollars_enabled", bearSizeContracts: "vs0_bear_size_contracts_enabled",
  // Daily entry caps — independent; hidden = not enforced by the agent.
  maxTradesDay: "vs0_max_trades_per_day_enabled",
  maxDollarsDay: "vs0_max_dollars_per_day_enabled",
  maxPctDay: "vs0_max_pct_account_per_day_enabled",
};

/** Enable keys that start hidden (false) when absent from account settings.
 *  Everything else in VS0_PARAM_ENABLE_KEY defaults to visible (true). */
const VS0_ENABLE_DEFAULT_FALSE = new Set([
  "vs0_avoid_fomc_enabled",
  "vs0_avoid_fomc_minutes_enabled",
  "vs0_total_exposure_dollars_enabled",
  "vs0_bull_size_dollars_enabled",
  "vs0_bull_size_contracts_enabled",
  "vs0_bear_size_dollars_enabled",
  "vs0_bear_size_contracts_enabled",
  "vs0_max_trades_per_day_enabled",
  "vs0_max_dollars_per_day_enabled",
  "vs0_max_pct_account_per_day_enabled",
]);

/** Fill missing `*_enabled` flags so hide/show state is always concrete in cfg
 *  (and therefore written on Save / Save as Defaults). Account rows win. */
export function applyEnableDefaults(cfg: Record<string, string>): Record<string, string> {
  const out = { ...cfg };
  for (const ek of Object.values(VS0_PARAM_ENABLE_KEY)) {
    if (out[ek] === undefined) {
      out[ek] = VS0_ENABLE_DEFAULT_FALSE.has(ek) ? "false" : "true";
    }
  }
  return out;
}

const on = (cfg: Record<string, string>, key: string) => cfg[key] !== "false";

/** Build the typed rating config from a string account-config map. */
export function buildRatingCfg(cfg: Record<string, string>): RatingCfg {
  return {
    baSpreadPctWeight: num(cfg.vs0_ba_spread_pct_weight, 50), baSpreadPctEnabled: on(cfg, "vs0_ba_spread_pct_enabled"),
    probItmWeight: num(cfg.vs0_prob_itm_weight, 50), probItmEnabled: on(cfg, "vs0_prob_itm_enabled"),
    hvPctWeight: num(cfg.vs0_hv_pct_weight, 0), hvPctEnabled: on(cfg, "vs0_hv_pct_enabled"),
    hvPctPoints: parsePoints(cfg.vs0_hv_pct_points, [[0, 100], [100, 100]]),
    hvPctLeftY: num(cfg.vs0_hv_pct_left_y, -1),
    hvPctRightY: num(cfg.vs0_hv_pct_right_y, 0),
    oiEnabled: on(cfg, "vs0_min_oi_enabled"),
    minOi: num(cfg.vs0_min_oi, 10), minOiLeftY: num(cfg.vs0_min_oi_left_y, -1), minOiRightY: num(cfg.vs0_min_oi_right_y, 0),
    baSpreadPctPoints: parsePoints(cfg.vs0_ba_spread_pct_points, [[5, 0], [10, 100], [20, 0]]),
    baSpreadPctLeftY: num(cfg.vs0_ba_spread_pct_left_y, 0),
    baSpreadPctRightY: num(cfg.vs0_ba_spread_pct_right_y, -1),
    probItmPoints: parsePoints(cfg.vs0_prob_itm_points, [[3, 0], [12, 100], [35, 0]]),
    probItmLeftY: num(cfg.vs0_prob_itm_left_y, -1),
    probItmRightY: num(cfg.vs0_prob_itm_right_y, -1),
    eProbItmWeight: num(cfg.vs0_e_prob_itm_weight, 0), eProbItmEnabled: num(cfg.vs0_e_prob_itm_weight, 0) > 0,
    eProbItmPoints: parsePoints(cfg.vs0_e_prob_itm_points, [[3, 0], [12, 100], [35, 0]]),
    eProbItmLeftY: num(cfg.vs0_e_prob_itm_left_y, -1),
    eProbItmRightY: num(cfg.vs0_e_prob_itm_right_y, -1),
    pProbItmWeight: num(cfg.vs0_p_prob_itm_weight, 0), pProbItmEnabled: num(cfg.vs0_p_prob_itm_weight, 0) > 0,
    pProbItmPoints: parsePoints(cfg.vs0_p_prob_itm_points, [[3, 0], [12, 100], [35, 0]]),
    pProbItmLeftY: num(cfg.vs0_p_prob_itm_left_y, -1),
    pProbItmRightY: num(cfg.vs0_p_prob_itm_right_y, -1),
    minPitmWeight: num(cfg.vs0_min_pitm_weight, 0), minPitmEnabled: on(cfg, "vs0_min_pitm_enabled"),
    minPitmPoints: parsePoints(cfg.vs0_min_pitm_points, [[3, 0], [12, 100], [35, 0]]),
    minPitmLeftY: num(cfg.vs0_min_pitm_left_y, 0),
    minPitmRightY: num(cfg.vs0_min_pitm_right_y, 0),
    minPitmSources: cfg.vs0_min_pitm_sources || "p,ep,pp",
    rorPoints: parsePoints(cfg.vs0_ror_points, [[50, 0], [200, 100]]),
    rorLeftY: num(cfg.vs0_ror_left_y, -1),
    rorRightY: num(cfg.vs0_ror_right_y, 0),
    rorWeight: num(cfg.vs0_ror_weight, 20),
    netCreditPoints: parsePoints(cfg.vs0_net_credit_points, [[0.6, 100], [2, 100]]),
    netCreditLeftY: num(cfg.vs0_net_credit_left_y, -1),
    netCreditRightY: num(cfg.vs0_net_credit_right_y, 0),
    netCreditWeight: num(cfg.vs0_net_credit_weight, 0),
    otmPoints: parsePoints(cfg.vs0_otm_points, [[0, 0], [10, 100]]),
    otmLeftY: num(cfg.vs0_otm_left_y, 0),
    otmRightY: num(cfg.vs0_otm_right_y, 0),
    otmWeight: num(cfg.vs0_otm_weight, 0), otmEnabled: on(cfg, "vs0_otm_enabled"),
    absReturnPoints: parsePoints(cfg.vs0_abs_return_points, [[0, 0], [20, 100]]),
    absReturnLeftY: num(cfg.vs0_abs_return_left_y, 0),
    absReturnRightY: num(cfg.vs0_abs_return_right_y, 0),
    absReturnWeight: num(cfg.vs0_abs_return_weight, 0), absReturnEnabled: on(cfg, "vs0_abs_return_enabled"),
    maxWidthPoints: parsePoints(cfg.vs0_max_spread_width_points, [[1, 100], [10, 100]]),
    maxWidthLeftY: num(cfg.vs0_max_spread_width_left_y, 0),
    maxWidthRightY: num(cfg.vs0_max_spread_width_right_y, -1),
    maxWidthWeight: num(cfg.vs0_max_spread_width_weight, 0), maxWidthEnabled: on(cfg, "vs0_max_spread_width_enabled"),
  };
}
